import TagManager from "react-gtm-module";
import type {TaxiOrderRequest, TaxiOverviewTierData} from "@/api/apiClient";
import {getDurationInMinutes} from "@/utils/datetime";

function logEvent(dataLayer: object) {
    return TagManager.dataLayer({dataLayer});
}

type SelectMethod = "map" | "input" | "geolocation";

function selectAddress({
    event,
    selectMethod,
}: {
    event: "select_pickup" | "select_dropoff";
    selectMethod: SelectMethod;
}) {
    return logEvent({
        event: event,
        select_method: selectMethod,
    });
}

type OverviewData = {
    pickupTime: number | null;
    dropoffBy: number | null;
    price: string | null; // price float (if min-max, use max)
    paymentMethod?: "apple_pay" | "google_pay" | "card_online" | "cash";
    orderId?: string;
};

function logOverview({
    event,
    ...rest
}: {event: "order_overview" | "payment_attempt" | "order_complete"} & OverviewData) {
    const snakeCaseRest = Object.keys(rest).reduce((acc, key) => {
        const snakeKey = key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
        // @ts-expect-error we know snakeKey exists in acc
        acc[snakeKey] = rest[key];
        return acc;
    }, {});

    return logEvent({
        event,
        ...snakeCaseRest,
    });
}

export const logGtm = {
    public: {
        selectPickup: (selectMethod: SelectMethod) =>
            selectAddress({event: "select_pickup", selectMethod}),
        selectDropoff: (selectMethod: SelectMethod) =>
            selectAddress({event: "select_dropoff", selectMethod}),
        orderOverview: (overviewData: OverviewData) =>
            logOverview({event: "order_overview", ...overviewData}),
        paymentAttempt: ({paymentMethod, orderId, ...rest}: OverviewData) =>
            logOverview({event: "payment_attempt", paymentMethod, orderId, ...rest}),
        orderComplete: ({paymentMethod, orderId, ...rest}: OverviewData) =>
            logOverview({event: "order_complete", paymentMethod, orderId, ...rest}),
    },
};

export function getOverviewLog(data: TaxiOverviewTierData | undefined) {
    return {
        price: data?.price?.final?.max?.amount ? data.price.final.max.amount.toFixed(2) : null,
        pickupTime: data?.arrivalDuration ? getDurationInMinutes(data.arrivalDuration) : null,
        dropoffBy: data?.rideDuration ? getDurationInMinutes(data.rideDuration) : null,
    };
}

export function getPaymentMethod(
    paymentType: TaxiOrderRequest["publicOrderPaymentType"] | undefined
) {
    switch (paymentType) {
        case "APPLE_PAY":
            return "apple_pay";
        case "GOOGLE_PAY":
            return "google_pay";
        case "CARD":
            return "card_online";
        default:
            return "cash";
    }
}
